import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import ProgressDot from "src/components/common/ProgressDot";
import { AnimatedBlueButton } from "src/components/common";
import { AchievementSwiper } from "src/components/home";
import { useTranslation } from "react-i18next";
import { SwiperRef } from "swiper/react";

export const AchievementsSection: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperRef | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isTextVisible, setIsTextVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (!textRef.current) return;
      const rect = textRef.current.getBoundingClientRect();
      const visible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
      setIsTextVisible(visible);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!swiperRef.current) return;

    const swiper = swiperRef.current.swiper;
    if (!isTextVisible) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
  }, [isTextVisible]);

  return (
    <Box sx={{ m: "0 auto", color: "#323e59" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflow: "hidden",
          m: "0 auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: "clamp(86px,7.292vw,140px) 0 clamp(86px,10.417vw,200px)",
          }}
        >
          <Box
            sx={{
              zIndex: 1,
              width: "100%",
              maxWidth: "1920px",
              m: "0 auto",
              display: "grid",
              gridTemplateColumns: "repeat(12,1fr)",
              columnGap: "clamp(10px,.833vw,16px)",
              paddingInline: "clamp(16px,4.167vw,80px)",
              position: "relative",
              overflow: "hidden",
              [theme.breakpoints.down("md")]: { display: "flex", gap: "40px", flexDirection: "column" },
            }}
          >
            <Box
              data-aos="fade-up"
              className="header"
              ref={textRef}
              sx={{
                display: "flex",
                textAlign: "center",
                justifyContent: "center",
                gridArea: "1/3/2/12",
              }}
            >
              <Typography
                sx={{
                  fontSize: "clamp(28px,4.167vw,80px)",
                  fontWeight: 500,
                }}
              >
                {t("home.achieve.title")}
              </Typography>
            </Box>
            <Box
              className="content"
              sx={{
                mt: "10vw",
                display: "flex",
                flexDirection: "row",
                gridArea: "2/2/3/13",
                gap: "clamp(10px,.833vw,16px)",
                [theme.breakpoints.down("md")]: { flexDirection: "column", gap: "40px", mt: "100px" },
              }}
            >
              <Box
                className="info"
                sx={{
                  width: "clamp(208px,22.292vw,428px)",
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  opacity: isTextVisible ? 0 : 1,
                  transition: "opacity .6s",
                  transform: "translate3d(0px, -8.982vh, 0px)",
                  [theme.breakpoints.down("md")]: { order: 1, width: "auto", paddingInline: "36px" },
                }}
              >
                <Typography
                  sx={{
                    letterSpacing: "normal",
                    fontSize: "clamp(28px, 3.125vw, 60px)",
                    fontWeight: 500,
                    lineHeight: "110%",
                    textTransform: "capitalize",
                  }}
                >
                  {t("home.achieve.sub_title")}
                </Typography>
                {swiperRef && (
                  <Box
                    sx={{
                      marginTop: "clamp(16px, 1.667vw, 32px)",
                      display: "flex",
                      flexDirection: "row",
                      gap: "clamp(6px, 0.521vw, 10px)",
                      [theme.breakpoints.down("md")]: { display: "none" },
                    }}
                  >
                    {Array.from({ length: 4 }, (_, i) => i).map(i => (
                      <Box key={i} onClick={() => swiperRef.current?.swiper.slideToLoop(i)}>
                        <ProgressDot isActive={i === activeIndex} />
                      </Box>
                    ))}
                  </Box>
                )}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "clamp(12px, 0.729vw, 14px)",
                    cursor: "pointer",
                    letterSpacing: "0.04em",
                    fontSize: "clamp(14px, 0.833vw, 16px)",
                    fontWeight: 500,
                    lineHeight: "140%",
                    color: "#364056",
                    mt: "clamp(16px, 2.344vw, 45px)",
                  }}
                >
                  <AnimatedBlueButton text="More Info" />
                </Box>
              </Box>
              <AchievementSwiper isTextVisible={isTextVisible} setActiveIndex={setActiveIndex} swiperRef={swiperRef} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default AchievementsSection;
